import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Save } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@quebon/ui/components/card'
import { Input } from '@quebon/ui/components/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@quebon/ui/components/select'
import { Spinner } from '@quebon/ui/components/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@quebon/ui/components/table'
import { cn } from '@quebon/ui/lib/utils'
import { adminApi } from '@/api/admin'
import { handleApiError } from '@/api/client'
import {
  WORLD_KEY_PATTERN,
  mathHeadWorldMappingApi,
  mathHeadWorldMappingQueryKeys,
  type MathHeadWorldMappingScope,
} from '@/api/mathHeadWorldMapping'

/**
 * 수학머리 World 매핑 (superadmin) — 누적 테스트 스코프(3챕터 블록) ↔ BIGGO World Key.
 * 매핑이 없는 스코프는 학습자가 수학머리 테스트를 시작할 수 없고(선생님 문의), 교사는 면제만 할 수 있다.
 * ADR 0009. superadmin 화면이라 챕터 코드·content id를 그대로 노출한다.
 */
export function MathHeadWorldMappingPage() {
  const queryClient = useQueryClient()
  const [curriculumId, setCurriculumId] = useState<string>('')
  const [drafts, setDrafts] = useState<Record<number, string>>({})

  const curriculaQuery = useQuery({
    queryKey: ['admin', 'contents', 'curricula-for-math-head'],
    queryFn: () => adminApi.getContents({ hierarchyLevel: 'curriculum', size: 500 }),
  })
  const curricula = useMemo(
    () => (curriculaQuery.data?.content ?? []).filter((c) => /과정[A-E]|LC-/i.test(c.title)),
    [curriculaQuery.data],
  )
  const curriculumItems = useMemo(
    () => curricula.map((c) => ({ value: String(c.id), label: `${c.title} (#${c.id})` })),
    [curricula],
  )

  const numericCurriculumId = Number(curriculumId)
  const listQuery = useQuery({
    queryKey: mathHeadWorldMappingQueryKeys.list(numericCurriculumId),
    queryFn: () => mathHeadWorldMappingApi.list(numericCurriculumId),
    enabled: numericCurriculumId > 0,
  })

  useEffect(() => {
    if (!listQuery.data) return
    setDrafts(Object.fromEntries(listQuery.data.scopes.map((scope) => [scope.scopeStartChapterContentId, scope.worldKey ?? ''])))
  }, [listQuery.data])

  const upsert = useMutation({
    mutationFn: ({ scopeStartChapterContentId, worldKey }: { scopeStartChapterContentId: number; worldKey: string }) =>
      mathHeadWorldMappingApi.upsert(numericCurriculumId, scopeStartChapterContentId, worldKey),
    onSuccess: (result) => {
      toast.success(result.worldKey ? `저장됨: ${result.worldKey}` : '매핑을 삭제했습니다.')
      void queryClient.invalidateQueries({ queryKey: mathHeadWorldMappingQueryKeys.list(numericCurriculumId) })
    },
    onError: (error) => toast.error(handleApiError(error)),
  })

  const scopes = listQuery.data?.scopes ?? []
  const missingCount = scopes.filter((scope) => !scope.worldKey).length

  return (
    <div className="flex flex-col gap-6 p-6">
      <Card>
        <CardHeader>
          <CardTitle>수학머리 World 매핑</CardTitle>
          <CardDescription>
            누적 테스트의 3챕터 스코프마다 BIGGO World Key를 지정합니다. 규칙: BIGGO world 이름 <code>LC-{'{과정}'}_{'{첫 챕터 코드}'}_{'{끝 챕터 코드}'}</code>
            의 key(예: <code>lc-a-001</code>). 매핑이 없는 스코프는 학습자가 시작할 수 없고 교사 면제만 가능합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="flex min-w-[320px] flex-col gap-1.5">
            <label htmlFor="math-head-curriculum" className="text-sm font-medium">커리큘럼</label>
            <Select
              items={curriculumItems}
              value={curriculumId}
              onValueChange={(value) => setCurriculumId(value ?? '')}
            >
              <SelectTrigger id="math-head-curriculum" className="w-full">
                <SelectValue placeholder={curriculaQuery.isLoading ? '불러오는 중…' : 'LC 커리큘럼 선택'} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {curriculumItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" disabled={!listQuery.data || listQuery.isFetching} onClick={() => void listQuery.refetch()}>
            <RefreshCw className={cn('mr-2 h-4 w-4', listQuery.isFetching && 'animate-spin')} />
            새로고침
          </Button>
          {listQuery.data ? (
            <div className="ml-auto flex items-center gap-2 text-sm text-slate-600">
              <span>스코프 {scopes.length}개</span>
              {missingCount > 0 ? (
                <Badge className="bg-red-600 text-white hover:bg-red-600">매핑 없음 {missingCount}</Badge>
              ) : (
                <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800">전부 매핑됨</Badge>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {curriculaQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>커리큘럼 목록을 불러오지 못했습니다</AlertTitle>
          <AlertDescription>{handleApiError(curriculaQuery.error)}</AlertDescription>
        </Alert>
      ) : null}
      {listQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>World 매핑을 불러오지 못했습니다</AlertTitle>
          <AlertDescription>{handleApiError(listQuery.error)}</AlertDescription>
        </Alert>
      ) : null}
      {listQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> 스코프를 불러오는 중…</div>
      ) : null}

      {listQuery.data ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{listQuery.data.curriculumTitle} <span className="text-slate-400">#{listQuery.data.curriculumContentId}</span></CardTitle>
            <CardDescription>스코프 = 레슨이 있는 챕터를 트리 순서로 3개씩 묶은 블록(누적 테스트 배치 규칙과 동일).</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>스코프 챕터 (코드 · 제목 · content id)</TableHead>
                  <TableHead className="w-[280px]">BIGGO World Key</TableHead>
                  <TableHead className="w-28">저장</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scopes.map((scope, index) => (
                  <ScopeRow
                    key={scope.scopeStartChapterContentId}
                    index={index + 1}
                    scope={scope}
                    draft={drafts[scope.scopeStartChapterContentId] ?? ''}
                    onDraftChange={(value) => setDrafts((prev) => ({ ...prev, [scope.scopeStartChapterContentId]: value }))}
                    saving={upsert.isPending && upsert.variables?.scopeStartChapterContentId === scope.scopeStartChapterContentId}
                    onSave={() => upsert.mutate({ scopeStartChapterContentId: scope.scopeStartChapterContentId, worldKey: drafts[scope.scopeStartChapterContentId] ?? '' })}
                  />
                ))}
                {scopes.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-slate-500">이 커리큘럼에는 누적 테스트 스코프가 없습니다.</TableCell></TableRow>
                ) : null}
              </TableBody>
            </Table>
            {listQuery.data.orphanMappings.length > 0 ? (
              <Alert className="mt-4">
                <AlertTitle>스코프 시작이 아닌 챕터에 남은 매핑 {listQuery.data.orphanMappings.length}건</AlertTitle>
                <AlertDescription>
                  {listQuery.data.orphanMappings.map((orphan) => (
                    <div key={orphan.scopeStartChapterContentId}>
                      chapter #{orphan.scopeStartChapterContentId} → #{orphan.scopeEndChapterContentId}: {orphan.worldKey}
                    </div>
                  ))}
                </AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}

function ScopeRow({
  index,
  scope,
  draft,
  onDraftChange,
  saving,
  onSave,
}: {
  index: number
  scope: MathHeadWorldMappingScope
  draft: string
  onDraftChange: (value: string) => void
  saving: boolean
  onSave: () => void
}) {
  const trimmed = draft.trim()
  const valid = trimmed === '' || WORLD_KEY_PATTERN.test(trimmed)
  const dirty = trimmed !== (scope.worldKey ?? '')
  const missing = !scope.worldKey
  return (
    <TableRow className={cn(missing && 'bg-red-50/60')}>
      <TableCell className="align-top text-slate-500">{index}</TableCell>
      <TableCell className="align-top">
        <div className="flex flex-col gap-0.5 text-sm">
          {scope.chapters.map((chapter) => (
            <span key={chapter.contentId}>
              <span className="text-slate-400">{chapter.ordinal}.</span> {chapter.title}
              <span className="ml-1 text-xs text-slate-400">#{chapter.contentId}</span>
            </span>
          ))}
          <span className="text-xs text-slate-400">배치 챕터 {scope.placementChapterOrdinal} · 스코프 {scope.scopeStartChapterOrdinal}–{scope.scopeEndChapterOrdinal}</span>
        </div>
      </TableCell>
      <TableCell className="align-top">
        <div className="flex flex-col gap-1">
          <Input
            aria-label={`스코프 ${index} World Key`}
            value={draft}
            placeholder="lc-a-001"
            onChange={(event) => onDraftChange(event.target.value)}
            className={cn(!valid && 'border-red-400')}
          />
          {missing ? <span className="text-xs font-medium text-red-600">매핑 없음</span> : null}
          {!valid ? <span className="text-xs text-red-600">[A-Za-z0-9_-] 1~64자</span> : null}
        </div>
      </TableCell>
      <TableCell className="align-top">
        <Button size="sm" disabled={!dirty || !valid || saving} onClick={onSave}>
          <Save className="mr-1 h-4 w-4" />
          {trimmed === '' && scope.worldKey ? '삭제' : '저장'}
        </Button>
      </TableCell>
    </TableRow>
  )
}
